(ns shared.core.components.recharts.line-chart
  (:require [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.style :as style]))

(def ^:private right-axis-id
  "Recharts axis id for the optional second value axis. The left axis keeps the
   default (0), so series without an explicit id still land on it."
  "right")

(defn line-chart-cmp [{:keys [container-props x-axis-visible? x-key x-axis-config y-axis-visible? y-axis-config
                              x-grid-visible? y-grid-visible? tooltip-visible? tooltip-config y-keys line-type
                              key->color y-key color animation-duration y-axis-width x-axis-height dot?
                              right-y-keys right-y-axis-config right-y-axis-width goal goal-config goal-label]
                       :or {color (get-in style/colors [:blue :shade-1])
                            x-axis-visible? true
                            y-axis-visible? false
                            x-grid-visible? false
                            y-grid-visible? true
                            tooltip-visible? true
                            dot? false
                            animation-duration 1000}}]
  (let [right-key? (set right-y-keys)
        ;; A key bound to the right axis is drawn once, there — not on both.
        left-y-keys (remove right-key? y-keys)
        ->line (fn [k right?]
                 [recharts.util/re-line (cond-> {:type line-type
                                                 :dataKey k
                                                 :stroke (key->color k)
                                                 :dot dot?
                                                 :animationDuration animation-duration}
                                                right?
                                                (assoc :yAxisId right-axis-id))])]
    (into [recharts.util/re-line-chart container-props
           [recharts.util/re-x-axis (merge {:height (if x-axis-visible? (or x-axis-height 12) 0)
                                            :dataKey x-key
                                            :tickLine false
                                            :interval "preserveStartEnd"
                                            :tick #(recharts.util/custom-rotated-x-tick % x-axis-config)}
                                           (recharts.util/strip-axis-options x-axis-config))]
           [recharts.util/re-y-axis (merge {:width (if y-axis-visible? (or y-axis-width 40) 0)
                                            :tickLine false
                                            :axisLine false
                                            :tick #(recharts.util/custom-y-tick % y-axis-config)}
                                           (recharts.util/axis-props y-axis-config))]
           (when (not-empty right-y-keys)
             [recharts.util/re-y-axis (merge {:yAxisId right-axis-id
                                              :orientation "right"
                                              :width (or right-y-axis-width 40)
                                              :tickLine false
                                              :axisLine false
                                              :tick #(recharts.util/custom-y-tick
                                                       %
                                                       (merge {:orientation :right}
                                                              right-y-axis-config))}
                                             (recharts.util/axis-props right-y-axis-config))])
           [recharts.util/re-cartesian-grid {:stroke (style/alc-color "lines/chart_grid")
                                             :vertical x-grid-visible?
                                             :horizontal y-grid-visible?}]
           (when tooltip-visible?
             [recharts.util/re-tooltip
              {:content (tooltip/chart-tooltip-content
                          {:header-fn (:header-transf-fn tooltip-config)
                           :label-fn (:label-transf-fn tooltip-config)
                           :value-fn (:value-transf-fn tooltip-config)
                           :row-fn (:row-fn tooltip-config)})}])
           (when goal
             (recharts.util/goal-line {:goal goal
                                       :goal-config goal-config
                                       :label goal-label}))]
          (if y-keys
            (concat (mapv #(->line % false) left-y-keys)
                    (mapv #(->line % true) right-y-keys))
            [[recharts.util/re-line {:type line-type
                                     :dataKey y-key
                                     :stroke color
                                     :dot dot?
                                     :animationDuration animation-duration}]]))))

(defn line-chart
  [{:keys [data y-keys colors data-keys-config smooth? show-legend? legend? legend-config legend-label-fn width min-width
           height margin scrollable? fill-width? chart-height chart-width chart-container-style chart-style x-axis-config
           series-key data-key point-width goal goal-config]
    :or {width 255
         min-width 255
         height 90
         chart-height 350
         smooth? true}
    :as args}]
  (let [{:keys [dynamic-margins? x-key y-key y-axis-config y-axis-visible? x-axis-visible?]
         :or {dynamic-margins? true
              x-axis-visible? true}} args
        [data y-keys] (recharts.util/pivot-series {:data data
                                                   :y-keys y-keys
                                                   :series-key series-key
                                                   :x-key x-key
                                                   :value-key (or y-key data-key "value")})
        show-legend? (if (some? legend?) legend? show-legend?)
        line-type (if smooth? "monotone" "linear")
        ;; The bar chart's per-series map, string or keyword keys at either level.
        key-config (fn [k]
                     (or (get data-keys-config k)
                         (get data-keys-config (name k))
                         (get data-keys-config (keyword k))))
        key->color (fn [k]
                     (let [cfg (key-config k)]
                       (or (when colors (get colors k))
                           (some #(get cfg %) [:stroke "stroke" :fill "fill"])
                           (recharts.util/color-from-string (name k)))))
        legend-entries (when (and show-legend? y-keys)
                         (mapv (fn [k]
                                 {:label (if legend-label-fn (legend-label-fn k) (name k))
                                  :color (key->color k)})
                               y-keys))
        ;; `point-width` is the horizontal room each x entry gets — lower it to fit
        ;; more of a long series on screen before it starts scrolling.
        chart-width (or chart-width (max (+ 100 (* (count data) (or point-width 60))) min-width))
        y-vals (if y-keys
                 (mapcat (fn [k] (map #(get % k) data)) y-keys)
                 (map #(get % y-key) data))
        max-value (or (recharts.util/axis-max y-axis-config)
                      (->> y-vals (filter number?) (reduce max (or goal 0))))
        y-axis-width (when (and dynamic-margins? y-axis-visible? (not (:width y-axis-config)))
                       (recharts.util/fit-y-axis-width [(recharts.util/tick-label y-axis-config max-value)]))
        x-axis-height (when (and dynamic-margins? x-axis-visible? (not (:height x-axis-config)))
                        (recharts.util/fit-x-axis-height
                          (map #(or (get % x-key) (get % (keyword x-key))) data)
                          (:angle x-axis-config)))
        goal-label (when goal
                     (recharts.util/goal-label goal goal-config (:percent? y-axis-config)))
        ;; Angled x labels run left of their tick and clip without room for it.
        margin (cond-> margin
                       (and margin x-axis-visible? (:angle x-axis-config))
                       (update :left #(max (or % 0)
                                           (- (recharts.util/fit-x-label-left-overflow
                                                (map (fn [d] (or (get d x-key) (get d (keyword x-key)))) data)
                                                (:angle x-axis-config))
                                              (or y-axis-width 0))))

                       goal
                       ((fn [m]
                          (update (or m {:top 20 :right 30 :left 20 :bottom 20})
                                  :right
                                  #(max (or % 0) (recharts.util/goal-right-margin goal-label))))))
        args (assoc args
                    :y-keys y-keys
                    :line-type line-type
                    :key->color key->color
                    :y-axis-width y-axis-width
                    :x-axis-height x-axis-height
                    :goal-label goal-label)]
    (if scrollable?
      [:div {:style (merge (cond-> {:width "100%"
                                    :display :flex
                                    :flex-direction :column
                                    :position :relative
                                    :gap 12}
                                   (not fill-width?)
                                   (assoc :align-items :center))
                           chart-container-style)}
       [recharts.util/scroll-frame {:fill-width? fill-width?
                                    :min-width (when fill-width? chart-width)
                                    :height chart-height
                                    :style chart-style}
        (if fill-width?
          (line-chart-cmp (assoc args
                                 :container-props (cond-> {:data (clj->js data)}
                                                          margin
                                                          (assoc :margin margin))
                                 :x-axis-config (merge {:interval 0}
                                                       x-axis-config)))
          [line-chart-cmp (assoc args :container-props {:data (clj->js data)
                                                        :responsive true
                                                        :width chart-width
                                                        :height chart-height
                                                        :margin (or margin {:top 20 :right 30 :left 20 :bottom 20})}
                                      :x-axis-config (merge {:interval 0}
                                                            x-axis-config))])]
       (when (not-empty legend-entries)
         [recharts.util/standalone-legend (assoc legend-config :entries legend-entries)])]
      [:div {:style {:width width
                     :display :flex
                     :flex-direction :column}}
       [recharts.util/re-responsive-container {:width width :height height}
        (line-chart-cmp (assoc args :container-props (cond-> {:data data}
                                                             margin
                                                             (assoc :margin margin))))]
       (when (not-empty legend-entries)
         [recharts.util/standalone-legend (assoc legend-config :entries legend-entries)])])))